import { createContext, useContext, type ReactNode } from "react";

/**
 * Context for providing client parameter throughout the Prompt feature
 */
const ClientContext = createContext<string | undefined>(undefined);

const PathRouteContext = createContext<string | undefined>(undefined);

/**
 * Provider component for client context
 * @param client - The client identifier (e.g., "gate")
 * @param children - React child components
 */
export function ClientProvider({
  client,
  children,
  path_route,
}: {
  client: string;
  children: ReactNode;
  path_route: string;
}) {
  return (
    <ClientContext.Provider value={client}>
      <PathRouteContext.Provider value={path_route}>
        {children}
      </PathRouteContext.Provider>
    </ClientContext.Provider>
  );
}

/**
 * Hook to access the client from context
 * @returns The client identifier
 * @throws Error if used outside of ClientProvider
 */
export function useClient(): string {
  const client = useContext(ClientContext);
  if (client === undefined) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return client;
}

/**
 * Hook to access the path route from context
 * @returns The path route
 * @throws Error if used outside of ClientProvider
 */
export function usePathRoute(): string {
  const path_route = useContext(PathRouteContext);
  if (path_route === undefined) {
    throw new Error("usePathRoute must be used within a ClientProvider");
  }
  return path_route;
}
